import React, { useEffect, useState } from "react";
import PrimaryButton from "../PrimaryButton";
import GreyButton from "../GreyButton";
import { useFormik } from "formik";
import RadioSelection from "../RadioSelection";
import DayBox from "./DayBox";
import { DatePicker, TimePicker } from "antd";
import { Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { CalendarOutlined } from "@ant-design/icons";
import { createOffer } from "../../../services/offerService";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ReactComponent as InfoIcon1 } from "../../../Assets/Icons/info.svg";
import Checkbox from "../Checkbox";
import { ReactComponent as UncheckedIcon } from "../../../Assets/Icons/uncheckedBlackBox.svg";
import Selector from "../Selector";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import Input from "../FormInputs";
import { isValidUrl } from "../../../utils/utilFunctions";
import SelectOption from "./SelectOption";
import alphanumericIcon from "../../../Assets/Icons/AlphanumericIcon.svg";
import qrIcon from "../../../Assets/Icons/QRicon.svg";
import barcodeIcon from "../../../Assets/Icons/Barcodeicon.svg";
import "./offerform.scss";
import { useOfferFormContext } from "./OfferFormContext";
import { scrollToFirstError } from "../../../utils/utilFunctions";

dayjs.extend(utc);
dayjs.extend(timezone);

const RedemptionFrequency = {
  ONCE: "ONCE",
  DAILY: "DAILY",
  IN_A_WEEK: "IN_A_WEEK",
  EVERY_30_DAYS: "EVERY_30_DAYS",
  EVERY_90_DAYS: "EVERY_90_DAYS",
  EVERY_120_DAYS: "EVERY_120_DAYS",
  ONCE_IN_12_MONTHS: "ONCE_IN_12_MONTHS",
  CONTINUALLY: "CONTINUALLY",
};

const CustomizeOffer = ({
  stepIndex,
  setStepIndex,
  handlePrevious,
  setPayloadData,
  editOfferData,
  isEdit,
  selectedMerchant,
  handleSaveAndExit,
}) => {
  const { registerNextAction, setIsNextLoading, setIsNextDisabled } = useOfferFormContext();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.offerReducer?.loading);
  const offerData = useSelector((state) => state?.offerReducer?.createdOffer);

  const [useDefaultTime, setUseDefaultTime] = useState(false);
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [modalContent, setModalContent] = useState("Alphanumeric Code");
  const [showUploadDocument, setShowUploadDocument] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const weekDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const format = "hh:mm A";

  const initialValues = {
    redemedOffer: "Alphanumeric Code",
    url: "",
    ruleSelection: "REDEEM_ONCE",
    rules: "",
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    days: [],
  };

  // Helper to map UI labels to backend frequency values
  const getFrequency = (ruleSel, ruleLbl) => {
    if (ruleSel === "CHOOSE_DATE_RANGE") return "";
    if (ruleSel === "OFFER_AVAILABLE_THROUGHOUT_YEAR") {
      if (ruleLbl === "Redeem Once Daily") return RedemptionFrequency.DAILY;
      if (ruleLbl === "Redeem Once Every Week") return RedemptionFrequency.IN_A_WEEK;
      if (ruleLbl === "Redeem Once Every Month") return RedemptionFrequency.EVERY_30_DAYS;
      if (ruleLbl === "Redeem Once Every Year") return RedemptionFrequency.ONCE_IN_12_MONTHS;
      if (ruleLbl === "Redeem Continually") return RedemptionFrequency.CONTINUALLY;
    }
    if (ruleSel === "REDEEM_ONCE") {
      if (ruleLbl === "Daily") return RedemptionFrequency.DAILY;
      if (ruleLbl === "Redeem Once") return RedemptionFrequency.ONCE;
      if (ruleLbl === "In a Week") return RedemptionFrequency.IN_A_WEEK;
      if (ruleLbl === "Every 30 Days") return RedemptionFrequency.EVERY_30_DAYS;
      if (ruleLbl === "Every 90 Days") return RedemptionFrequency.EVERY_90_DAYS;
      if (ruleLbl === "Every 120 Days") return RedemptionFrequency.EVERY_120_DAYS;
      if (ruleLbl === "Once in 12 Months") return RedemptionFrequency.ONCE_IN_12_MONTHS;
    }
    return "";
  };

  const checkFieldApplicability = (ruleSel, frequency) => {
    const isDateApplicable = (
      (ruleSel === "CHOOSE_DATE_RANGE") ||
      (ruleSel === "REDEEM_ONCE" && frequency !== "ONCE_IN_12_MONTHS" && frequency !== "") ||
      (ruleSel === "OFFER_AVAILABLE_THROUGHOUT_YEAR" && !["ONCE", "CONTINUALLY", ""].includes(frequency))
    );

    const isDayApplicable = (
      (ruleSel === "CHOOSE_DATE_RANGE") ||
      (ruleSel === "REDEEM_ONCE" && !["DAILY", "ONCE_IN_12_MONTHS", ""].includes(frequency)) ||
      (ruleSel === "OFFER_AVAILABLE_THROUGHOUT_YEAR" && !["ONCE", "CONTINUALLY", ""].includes(frequency))
    );

    const isTimeApplicable = (
      (ruleSel === "CHOOSE_DATE_RANGE") ||
      (ruleSel === "REDEEM_ONCE" && frequency !== "ONCE_IN_12_MONTHS" && frequency !== "") ||
      (ruleSel === "OFFER_AVAILABLE_THROUGHOUT_YEAR" && !["ONCE", "CONTINUALLY", ""].includes(frequency))
    );

    return { isDateApplicable, isDayApplicable, isTimeApplicable };
  };

  const customizeOfferValidation = (values) => {
    const errors = {};
    const { ruleSelection, rules, startDate, endDate, startTime, endTime, days, url, redemedOffer } = values;
    const frequency = getFrequency(ruleSelection, rules);
    const { isDateApplicable, isDayApplicable, isTimeApplicable } = checkFieldApplicability(ruleSelection, frequency);

    if (!rules && ruleSelection !== "CHOOSE_DATE_RANGE") {
      errors.rules = "Required";
    }

    if (isDateApplicable) {
      if (!startDate) errors.startDate = "Start date is required";
      if (!endDate) errors.endDate = "End date is required";

      const start = dayjs(startDate).startOf('day');
      const end = dayjs(endDate).startOf('day');
      const today = dayjs().startOf('day');

      if (!isEdit && startDate && start.isBefore(today)) {
        errors.startDate = "Start date cannot be in the past";
      }

      if (startDate && endDate && end.isBefore(start)) {
        errors.endDate = "End date must be after or equal to start date";
      }
    }

    if (isDayApplicable) {
      // Redemption Days are optional for CHOOSE_DATE_RANGE according to payload specs
      if (ruleSelection !== "CHOOSE_DATE_RANGE" && (!days || days.length === 0)) {
        errors.days = "Please select at least one day";
      }
    }

    if (useDefaultTime && isTimeApplicable) {
      if (!startTime) errors.startTime = "Start time is required";
      if (!endTime) errors.endTime = "End time is required";
      if (startTime && endTime && dayjs(endTime).isSame(dayjs(startTime))) {
        errors.endTime = "End time must be different from start time";
      }
    }

    if (url && !isValidUrl(url)) {
      errors.url = "Invalid URL format";
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: initialValues,
    validate: customizeOfferValidation,
    onSubmit: async (values) => {
      const createPromise = await dispatch(
        createOffer({
          ...payload,
          offerId: isEdit ? editOfferData?._id : offerData?._id,
        })
      )?.unwrap();
      if (createPromise?.success === true) {
        setIsSubmitted(true);
        setStepIndex(stepIndex + 1);
      }
    },
  });

  const {
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
    setValues,
    setFieldTouched,
  } = formik;

  const {
    redemedOffer,
    url,
    ruleSelection,
    rules,
    startDate,
    endDate,
    startTime,
    endTime,
    days,
  } = formik.values;

  const fillUpForm = (data) => {
    if (data?.redemptionMethod) {
      const method = data.redemptionMethod;
      const formattedMethod =
        method === "QR_CODE"
          ? "QR Code"
          : method === "BAR_CODE"
            ? "BarCode"
            : method === "SWOODLE_CODE_OFFER"
              ? "Alphanumeric Code"
              : method;
      setFieldValue("redemedOffer", formattedMethod);
    }

    if (data?.callback_url) {
      setFieldValue("url", data.callback_url);
    }

    const necessaryRule = data?.necessaryRule || "REDEEM_ONCE";
    setFieldValue("ruleSelection", necessaryRule);

    if (data?.redemptionFrequency) {
      let ruleValue = "";
      if (necessaryRule === "OFFER_AVAILABLE_THROUGHOUT_YEAR") {
        switch (data.redemptionFrequency) {
          case RedemptionFrequency.DAILY: ruleValue = "Redeem Once Daily"; break;
          case RedemptionFrequency.ONCE: ruleValue = "Redeem Once Daily"; break;
          case RedemptionFrequency.IN_A_WEEK: ruleValue = "Redeem Once Every Week"; break;
          case RedemptionFrequency.EVERY_30_DAYS: ruleValue = "Redeem Once Every Month"; break;
          case RedemptionFrequency.ONCE_IN_12_MONTHS: ruleValue = "Redeem Once Every Year"; break;
          case RedemptionFrequency.CONTINUALLY: ruleValue = "Redeem Continually"; break;
          default: ruleValue = "";
        }
      } else if (necessaryRule === "REDEEM_ONCE") {
        switch (data.redemptionFrequency) {
          case RedemptionFrequency.DAILY: ruleValue = "Daily"; break;
          case RedemptionFrequency.ONCE: ruleValue = "Redeem Once"; break;
          case RedemptionFrequency.IN_A_WEEK: ruleValue = "In a Week"; break;
          case RedemptionFrequency.EVERY_30_DAYS: ruleValue = "Every 30 Days"; break;
          case RedemptionFrequency.EVERY_90_DAYS: ruleValue = "Every 90 Days"; break;
          case RedemptionFrequency.EVERY_120_DAYS: ruleValue = "Every 120 Days"; break;
          case RedemptionFrequency.ONCE_IN_12_MONTHS: ruleValue = "Once in 12 Months"; break;
          default: ruleValue = "";
        }
      } else if (necessaryRule === "CHOOSE_DATE_RANGE") {
        setFieldValue("ruleSelection", "CHOOSE_DATE_RANGE");
        ruleValue = "";
      }
      setFieldValue("rules", ruleValue);
    }

    setFieldValue("startDate", data?.startDate ? dayjs(data?.startDate) : "");
    setFieldValue("endDate", data?.endDate ? dayjs(data?.endDate) : "");

    if (data?.startTime && data?.endTime) {
      const isDefault = data?.startTime === "12:01 AM" && data?.endTime === "11:59 PM";
      setUseDefaultTime(!isDefault);
      if (!isDefault) {
        setFieldValue("startTime", dayjs(data?.startTime, format));
        setFieldValue("endTime", dayjs(data?.endTime, format));
      }
    }

    const tradingDays = data?.redemptionDays?.map(
      (day) => day?.charAt(0)?.toUpperCase() + day?.slice(1)?.toLowerCase()
    );
    setFieldValue("days", tradingDays || []);
  };

  const payload = React.useMemo(() => {
    const frequency = getFrequency(ruleSelection, rules);
    const { isDateApplicable, isDayApplicable, isTimeApplicable } = checkFieldApplicability(ruleSelection, frequency);

    const p = {
      necessaryRule: (rules || ruleSelection === "CHOOSE_DATE_RANGE") ? ruleSelection : null,
      id: isEdit ? editOfferData?._id : offerData?._id,
      merchantId: selectedMerchant?._id,
      isCreated: false,
      callback_url: url || null,
      redemptionMethod:
        redemedOffer === "QR Code" ? "QR_CODE" :
          redemedOffer === "BarCode" ? "BAR_CODE" : "SWOODLE_CODE_OFFER",
    };

    if (ruleSelection !== "CHOOSE_DATE_RANGE") {
      p.redemptionFrequency = frequency;
    }

    if (isDateApplicable) {
      if (startDate) p.startDate = dayjs(startDate).toISOString();
      if (endDate) p.endDate = dayjs(endDate).toISOString();
    }

    if (isTimeApplicable && useDefaultTime) {
      if (startTime) p.startTime = dayjs(startTime).format(format);
      if (endTime) p.endTime = dayjs(endTime).format(format);
    }

    if (isDayApplicable && days && days.length > 0) {
      p.redemptionDays = days.map(d => d.toUpperCase());
    }

    return p;
  }, [rules, ruleSelection, startDate, endDate, startTime, endTime, days, offerData, editOfferData, isEdit, selectedMerchant, url, redemedOffer, useDefaultTime]);

  useEffect(() => {
    setPayloadData(payload);
  }, [payload, setPayloadData]);

  useEffect(() => {
    const sourceData = offerData?._id ? offerData : editOfferData;
    if (sourceData) fillUpForm(sourceData);
  }, [offerData, editOfferData]);

  const attemptNext = async () => {
    // Manually mark fields as touched so errors appear
    const touchedFields = Object.keys(initialValues).reduce((acc, key) => ({ ...acc, [key]: true }), {});
    if (useDefaultTime) {
      touchedFields.startTime = true;
      touchedFields.endTime = true;
    }
    formik.setTouched(touchedFields);

    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors).length > 0) {
      scrollToFirstError(validationErrors, ["rules", "startDate", "endDate", "startTime", "endTime", "days", "url"]);
      return;
    }
    formik.handleSubmit();
  };

  useEffect(() => {
    registerNextAction(() => attemptNext());
  }, [registerNextAction, formik.values, useDefaultTime]);

  useEffect(() => {
    setIsNextLoading(loading);
  }, [loading, setIsNextLoading]);

  // Update disabled state for Next button
  useEffect(() => {
    const errors = customizeOfferValidation(formik.values);
    setIsNextDisabled(Object.keys(errors).length > 0);
  }, [formik.values, useDefaultTime, setIsNextDisabled]);

  // Handle side effects when frequency changes
  useEffect(() => {
    const freq = getFrequency(ruleSelection, rules);
    const { isTimeApplicable } = checkFieldApplicability(ruleSelection, freq);
    if (!isTimeApplicable && useDefaultTime) {
      setUseDefaultTime(false);
      setFieldValue("startTime", null);
      setFieldValue("endTime", null);
    }
  }, [ruleSelection, rules]);

  const handleDefaultTimeToggle = () => {
    const newVal = !useDefaultTime;
    setUseDefaultTime(newVal);
    if (!newVal) {
      setFieldValue("startTime", null);
      setFieldValue("endTime", null);
    }
  };

  const handleDayClick = (day) => {
    const currentDays = days || [];
    if (currentDays.includes(day)) {
      setFieldValue("days", currentDays.filter(d => d !== day));
    } else {
      setFieldValue("days", [...currentDays, day]);
    }
  };

  const isDayClicked = (day) => days?.includes(day);

  const handleSwoodleCodeChange = (code) => setFieldValue("redemedOffer", code);

  const handleRuleSelectionChange = (e) => {
    setFieldValue("ruleSelection", e);
    setFieldValue("rules", "");
    setFieldValue("days", []);
  };

  const isNextButtonBusy = loading;
  const radioOptions = ruleSelection === "OFFER_AVAILABLE_THROUGHOUT_YEAR"
    ? ["Redeem Once Daily", "Redeem Once Every Week", "Redeem Once Every Month", "Redeem Once Every Year", "Redeem Continually"]
    : ["Daily", "In a Week", "Every 30 Days", "Every 90 Days", "Every 120 Days", "Once in 12 Months"];

  const rulesOptions = [
    { label: "Redeem Once", value: "REDEEM_ONCE" },
    { label: "Offer Available throughout the Year", value: "OFFER_AVAILABLE_THROUGHOUT_YEAR" },
    { label: "Choose a Date Range", value: "CHOOSE_DATE_RANGE" },
  ];

  return (
    <div className="w-full flex flex-col items-center">
      <div className="w-full max-w-[1100px] relative mt-12 sm:mt-20">
        {/* Save and Exit at Top Right of the main container */}
        <div className="fixed top-[0px] right-4 md:right-2 z-[200]">
          <button
            onClick={handleSaveAndExit}
            className="px-4 sm:px-6 h-[36px] sm:h-[40px] border border-[#00ADEF] rounded-[5px] text-[#00ADEF] text-[12px] sm:text-[14px] font-[500] font-poppins hover:bg-[#F0FBFF] transition-all"
          >
            Save and Exit
          </button>
        </div>

        {/* Center content at 650px width */}
        <div className="w-full max-w-[650px] mx-auto mb-10">
          <div className="w-full mb-8">
            <h2 className="text-[28px] sm:text-[32px] font-[600] text-[#303030] font-poppins">
              Customise <span className="text-[#00ADEF]">Your Offer</span>
            </h2>
            <p className="text-[#767676] text-[13px] sm:text-[14px] font-[400] font-poppins mt-1">
              Let customers know how and when they can use your offer.
            </p>
          </div>

          {/* 1. Your Redemption Method Card */}
          {offerData?.offerType !== "ONLINE_OFFER" && (
            <div className="bg-white border border-[#F0F0F0] rounded-[15px] p-6 mb-6 shadow-[0px_8px_30px_0px_rgba(0,0,0,0.06)]">
              <h3 className="text-[16px] font-[600] text-[#303030] font-poppins mb-1">
                Your Redemption Method<span className="text-red-500">*</span>
              </h3>
              <p className="text-[#767676] text-[13px] font-[400] font-poppins mb-6">
                Select the option that matches how customers will use this offer.
              </p>
              <div className="flex flex-col gap-3">
                {[
                  { label: "Alphanumeric Code", icon: alphanumericIcon, display: "Swoodle Code Offer", sub: "eg. SWD8XK9" },
                  { label: "QR Code", icon: qrIcon, display: "QR Code Offer" },
                  { label: "BarCode", icon: barcodeIcon, display: "Bar Code Offer" }
                ].map(opt => (
                  <div
                    key={opt.label}
                    onClick={() => handleSwoodleCodeChange(opt.label)}
                    className={`relative border rounded-[5px] h-[45px] w-full flex items-center justify-between cursor-pointer transition-all px-4 ${redemedOffer === opt.label ? "bg-[#EBF9FE] border-[#00ADEF]" : "bg-white border-[#D9D9D9]"}`}
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={opt.icon}
                        alt=""
                        className={`w-5 h-5 transition-all ${redemedOffer === opt.label ? "" : "grayscale brightness-90 opacity-85"}`}
                      />
                      <span className={`text-[14px] font-poppins ${redemedOffer === opt.label ? "text-[#00ADEF] font-[500]" : "text-[#303030] font-[400]"}`}>
                        {opt.display}
                      </span>
                    </div>
                    {opt.sub && <span className="text-[#00ADEF] text-[12px] opacity-70">{opt.sub}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 2. Callback URL Option */}
          <div className="bg-white border border-[#F0F0F0] rounded-[15px] p-6 mb-6 shadow-[0px_8px_30px_0px_rgba(0,0,0,0.06)]">
            <h3 className="text-[16px] font-[600] text-[#303030] font-poppins mb-2">
              Callback URL Option
            </h3>

            <input
              type="text"
              name="url"
              value={url}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Click to add your callback url"
              className="w-full h-[45px] px-3 rounded-[5px] border border-[#D9D9D9] outline-none focus:border-[#00ADEF] transition-all font-poppins text-sm text-[#303030] mb-3"
            />

            <div className="flex items-start gap-2">
              <div className="mt-[2px] min-w-[20px]">
                <InfoIcon1 className="w-5 h-5 text-[#00ADEF]" />
              </div>
              <p className="mt-[3px] text-[13px] text-[#767676] font-poppins leading-[18px]">
                Add a callback URL to receive real-time redemption notifications from <br /> Swoodle.
              </p>
            </div>

            {errors.url && touched.url && <span className="text-red-600 text-[11px] mt-1 block">{errors.url}</span>}
          </div>

          {/* 3. Necessary Rules Card */}
          <div className="bg-white border border-[#F0F0F0] rounded-[15px] p-6 mb-6 shadow-[0px_8px_30px_0px_rgba(0,0,0,0.06)]">
            <h3 className="text-[16px] font-[600] text-[#303030] font-poppins mb-4">Necessary Rules<span className="text-red-500">*</span></h3>
            <div className="w-full mb-4">
              <Select
                value={ruleSelection}
                onChange={handleRuleSelectionChange}
                placeholder={<span className="text-[#303030]">Select Rule</span>}
                className="w-full h-[45px] font-poppins text-[#303030] text-sm custom-select-rule"
                suffixIcon={<DownArrow className="w-3 h-3 text-[#767676]" />}
                getPopupContainer={(trigger) => trigger.parentNode}
                options={rulesOptions}
                style={{ borderRadius: "5px" }}
              />
            </div>

            {(ruleSelection === "OFFER_AVAILABLE_THROUGHOUT_YEAR" || ruleSelection === "REDEEM_ONCE") && (
              <div className="mb-1" data-field="rules">
                <div className="flex flex-wrap gap-x-3 gap-y-2">
                  {radioOptions.map((option, index) => (
                    <React.Fragment key={option}>
                      <div
                        onClick={() => {
                          setFieldValue("rules", option);
                          // Auto-populate days for Daily/Continually/Yearly
                          if (["Daily", "Redeem Once Daily", "Redeem Continually"].includes(option)) {
                            setFieldValue("days", weekDays);
                          } else {
                            // Reset days for other frequencies (In a Week, Every 30/90/120 Days)
                            setFieldValue("days", []);
                          }
                        }}
                        className={`h-[40px] px-4 whitespace-nowrap min-w-[100px] w-auto flex items-center justify-center rounded-[5px] border-[1.5px] cursor-pointer transition-all text-[11px] sm:text-[13px] font-poppins text-center ${rules === option ? "bg-[#EBF9FE] border-[#00ADEF] text-[#00ADEF] font-[500]" : "bg-white border-[#D9D9D9] text-[#000000] hover:border-[#00ADEF]"}`}
                      >
                        {option}
                      </div>
                      {(index + 1) % (ruleSelection === "OFFER_AVAILABLE_THROUGHOUT_YEAR" ? 2 : 3) === 0 && <div className="w-full h-0" />}
                    </React.Fragment>
                  ))}
                </div>
                {errors.rules && touched.rules && <span className="text-red-600 text-[11px] mt-2 block">{errors.rules}</span>}
              </div>
            )}

            {/* Visibility Check for Start/End Date */}
            {(() => {
              const freq = getFrequency(ruleSelection, rules);
              const { isDateApplicable } = checkFieldApplicability(ruleSelection, freq);

              return (
                <div className={`offer-reveal-container ${isDateApplicable ? 'expanded mt-4' : ''}`}>
                  <div className="space-y-6">
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="flex-1">
                        <p className="text-[14px] font-[600] text-[#303030] mb-1 font-poppins">Start Date</p>
                        <DatePicker
                          className="w-full h-[45px] rounded-[5px] border-[#D9D9D9] font-poppins"
                          format="DD/MM/YYYY"
                          placeholder="DD/MM/YYYY"
                          value={startDate ? dayjs(startDate) : null}
                          onChange={d => setFieldValue("startDate", d ? d.toDate() : null)}
                          disabledDate={(current) => current && current < dayjs().startOf('day')}
                          suffixIcon={<CalendarOutlined className="text-[#00ADEF]" />}
                        />
                        {errors.startDate && touched.startDate && <span className="text-red-600 text-[11px] font-poppins">{errors.startDate}</span>}
                      </div>
                      <div className="flex-1">
                        <p className="text-[14px] font-[600] text-[#303030] mb-1 font-poppins">End Date</p>
                        <DatePicker
                          className="w-full h-[45px] rounded-[5px] border-[#D9D9D9] font-poppins"
                          format="DD/MM/YYYY"
                          placeholder="DD/MM/YYYY"
                          value={endDate ? dayjs(endDate) : null}
                          onChange={d => setFieldValue("endDate", d ? d.toDate() : null)}
                          disabledDate={(current) => {
                            if (!current) return false;
                            const isPast = current < dayjs().startOf('day');
                            const isBeforeStart = startDate ? current < dayjs(startDate).startOf('day') : false;
                            return isPast || isBeforeStart;
                          }}
                          suffixIcon={<CalendarOutlined className="text-[#00ADEF]" />}
                        />
                        {errors.endDate && touched.endDate && <span className="text-red-600 text-[11px] font-poppins">{errors.endDate}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Checkbox for Specific Hours - Only visible if time is applicable */}
            {(() => {
              const freq = getFrequency(ruleSelection, rules);
              const { isTimeApplicable } = checkFieldApplicability(ruleSelection, freq);
              if (!isTimeApplicable) return null;

              return (
                <div className="mt-4 flex items-center gap-2">
                  <div
                    onClick={handleDefaultTimeToggle}
                    className={`min-w-[20px] h-5 rounded border flex items-center justify-center cursor-pointer transition-all ${useDefaultTime ? "bg-[#00ADEF] border-[#00ADEF]" : "bg-white border-[#D9D9D9]"}`}
                  >
                    {useDefaultTime && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                  </div>
                  <span className="text-[13px] sm:text-[14px] font-poppins text-[#000000] leading-[1.4] select-none">
                    Check to set specific hours, leave unchecked for all time availability
                  </span>
                </div>
              );
            })()}

            {/* Dropdown for Days and Times */}
            {(() => {
              const freq = getFrequency(ruleSelection, rules);
              const { isDayApplicable, isTimeApplicable } = checkFieldApplicability(ruleSelection, freq);

              // We expand this section if:
              // 1. User checked "specific hours" AND time is applicable
              // 2. OR the selection REQUIRES day selection
              const hasBaseRule = !!rules || ruleSelection === "CHOOSE_DATE_RANGE";
              const sectionExpanded = hasBaseRule && ((useDefaultTime && isTimeApplicable) || isDayApplicable);

              return (
                <div className={`offer-reveal-container ${sectionExpanded ? 'expanded' : ''}`}>
                  <div className="space-y-4">
                    {/* Day Selection */}
                    {isDayApplicable && (
                      <div className="w-full mt-4">
                        <p className="text-[14px] font-[600] text-[#303030] mb-3 font-poppins">Choose the applicable days</p>
                        <DayBox
                          days={weekDays}
                          handleDayClick={handleDayClick}
                          isDayClicked={(day) => days?.includes(day)}
                        />
                        {errors.days && touched.days && <span className="text-red-600 text-[11px] mt-2 block font-poppins">{errors.days}</span>}
                      </div>
                    )}

                    {/* Time Selection - Only if checkbox is checked */}
                    {useDefaultTime && isTimeApplicable && (
                      <div className={`flex flex-col md:flex-row gap-4 ${isDayApplicable ? '' : 'mt-4'}`}>
                        <div className="flex-1">
                          <p className="text-[14px] font-[600] text-[#303030] mb-1 font-poppins">Start Time</p>
                          <TimePicker
                            className="w-full h-[45px] rounded-[5px] border-[#D9D9D9] font-poppins"
                            format={format}
                            use12Hours
                            placeholder="9:00 AM"
                            value={startTime ? dayjs(startTime, format) : null}
                            onChange={t => setFieldValue("startTime", t)}
                          />
                          {errors.startTime && touched.startTime && <span className="text-red-600 text-[11px] font-poppins">{errors.startTime}</span>}
                        </div>
                        <div className="flex-1">
                          <p className="text-[14px] font-[600] text-[#303030] mb-1 font-poppins">End Time</p>
                          <TimePicker
                            className="w-full h-[45px] rounded-[5px] border-[#D9D9D9] font-poppins"
                            format={format}
                            use12Hours
                            placeholder="5:00 PM"
                            value={endTime ? dayjs(endTime, format) : null}
                            onChange={t => setFieldValue("endTime", t)}
                          />
                          {errors.endTime && touched.endTime && <span className="text-red-600 text-[11px] font-poppins">{errors.endTime}</span>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomizeOffer;